import axios from 'axios';

const API_URL = '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors (Unauthorized)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Password temporária: o servidor só deixa trocá-la. O ecrã (AuthContext) mostra o formulário de troca.
    if (error.response?.status === 403 && error.response?.data?.code === 'PASSWORD_CHANGE_REQUIRED') {
      try { window.dispatchEvent(new Event('gymag:password-change-required')); } catch { /* sem window */ }
    }
    if (error.response?.status === 401) {
      // Token expired or invalid - clear storage and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const auth = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (name, email, password, phone) => 
    api.post('/auth/register', { name, email, password, phone }),
  // Quem está autenticado, segundo o SERVIDOR: é o que decide de quem é o plano que o atleta gera
  // (o atleta nunca vem do corpo do pedido nem do que o browser tem guardado).
  me: () => api.get('/auth/me'),
  // Troca a password (a atual é pedida sempre). Devolve um token novo, sem a marca de password temporária.
  changePassword: (currentPassword, newPassword) => api.post('/auth/change-password', { currentPassword, newPassword }),
};

// Users endpoints
export const users = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  toggleActive: (id) => api.patch(`/users/${id}/toggle-active`),
  updateMaxBookings: (id, maxBookings) => 
    api.patch(`/users/${id}/max-bookings`, maxBookings),
  updateMe: (data) => api.patch('/users/me', data),
};

// TimeSlots endpoints
export const timeSlots = {
  getAll: (params) => api.get('/timeslots', { params }),
  getById: (id) => api.get(`/timeslots/${id}`),
  create: (data) => api.post('/timeslots', data),
  createBulk: (data) => api.post('/timeslots/bulk', data),
  update: (id, data) => api.put(`/timeslots/${id}`, data),
  block: (id) => api.patch(`/timeslots/${id}/block`),
  unblock: (id) => api.patch(`/timeslots/${id}/unblock`),
  delete: (id) => api.delete(`/timeslots/${id}`),
};

// Bookings endpoints
export const bookings = {
  getAll: (params) => api.get('/bookings', { params }),
  getById: (id) => api.get(`/bookings/${id}`),
  create: (data) => api.post('/bookings', data),
  adminUpdate: (id, data) => api.put(`/bookings/${id}/admin`, data),
  approve: (id) => api.post(`/bookings/${id}/approve`),
  cancel: (id, reason) => api.patch(`/bookings/${id}/cancel`, { reason }),
  delete: (id) => api.delete(`/bookings/${id}`),
  checkIn: (id) => api.post(`/bookings/${id}/checkin`),
  markNoShow: (id) => api.post(`/bookings/${id}/noshow`),
  // Marcação ao balcão: a receção marca em nome do membro (Admin). O servidor responde com um
  // `outcome` que diz o que fazer a seguir (booked, slot-full → lista de espera, etc.).
  onBehalf: (data) => api.post('/bookings/on-behalf', data),
};

// WorkoutPlans endpoints
//
// O PUT do plano só altera o cabeçalho (título, datas, notas). Dias e exercícios mudam pelos
// endpoints próprios, que mantêm os Ids: é por eles que o histórico de execuções liga ao plano.
export const workoutPlans = {
  getAll: (params) => api.get('/workoutplans', { params }),
  getById: (id) => api.get(`/workoutplans/${id}`),
  create: (data) => api.post('/workoutplans', data),
  update: (id, data) => api.put(`/workoutplans/${id}`, data),
  toggleActive: (id) => api.patch(`/workoutplans/${id}/toggle-active`),
  delete: (id) => api.delete(`/workoutplans/${id}`),

  // Modelos: copiar (modelo ou plano → modelo ou plano de um sócio) e atribuir um modelo a um sócio.
  copy: (id, data) => api.post(`/workoutplans/${id}/copy`, data),
  assign: (id, data) => api.post(`/workoutplans/${id}/assign`, data),

  // Dias
  addDay: (id, data) => api.post(`/workoutplans/${id}/days`, data),
  updateDay: (id, dayId, data) => api.put(`/workoutplans/${id}/days/${dayId}`, data),
  deleteDay: (id, dayId) => api.delete(`/workoutplans/${id}/days/${dayId}`),
  reorderDays: (id, ids) => api.put(`/workoutplans/${id}/days/order`, { ids }),

  // Exercícios (o Id da linha nunca muda; só o conteúdo)
  addExercise: (id, dayId, data) => api.post(`/workoutplans/${id}/days/${dayId}/exercises`, data),
  // Superset/circuito: agrupar 2–3 exercícios do dia (o descanso passa a ser entre rondas) e desagrupar.
  groupSuperset: (id, dayId, exerciseIds) => api.post(`/workoutplans/${id}/days/${dayId}/superset`, { exerciseIds }),
  ungroupSuperset: (id, dayId, exerciseId) => api.delete(`/workoutplans/${id}/days/${dayId}/exercises/${exerciseId}/superset`),
  updateExercise: (id, exerciseId, patch) => api.put(`/workoutplans/${id}/exercises/${exerciseId}`, patch),
  removeExercise: (id, exerciseId) => api.delete(`/workoutplans/${id}/exercises/${exerciseId}`),
  reorderExercises: (id, dayId, ids) => api.put(`/workoutplans/${id}/days/${dayId}/exercises/order`, { ids }),
  replaceExercise: (id, exerciseId, libraryExerciseId) =>
    api.post(`/workoutplans/${id}/exercises/${exerciseId}/replace`, { libraryExerciseId }),

  // Gerador de planos: devolve uma PROPOSTA (nada se grava); guarda-se depois com `create`.
  generate: (request) => api.post('/workoutplans/generate', request),
  // O mesmo motor, mas para o próprio atleta: o servidor força o atleta da sessão (ignora qualquer atleta
  // no corpo) e devolve a mesma proposta. Continua a não gravar nada — guarda-se com `create`.
  generateMine: (request) => api.post('/workoutplans/mine/generate', request),
};

// MealPlans endpoints
export const mealPlans = {
  getAll: (params) => api.get('/mealplans', { params }),
  getByUser: (userId) => api.get(`/mealplans/user/${userId}`),
  getById: (id) => api.get(`/mealplans/${id}`),
  create: (data) => api.post('/mealplans', data),
  update: (id, data) => api.put(`/mealplans/${id}`, data),
  toggleActive: (id) => api.patch(`/mealplans/${id}/toggle-active`),
  delete: (id) => api.delete(`/mealplans/${id}`),
  getStats: () => api.get('/mealplans/stats'),
};

// OnlineClasses endpoints
export const onlineClasses = {
  getAll: (params) => api.get('/onlineclasses', { params }),
  getById: (id) => api.get(`/onlineclasses/${id}`),
  create: (data) => api.post('/onlineclasses', data),
  update: (id, data) => api.put(`/onlineclasses/${id}`, data),
  delete: (id) => api.delete(`/onlineclasses/${id}`),
  getCategories: () => api.get('/onlineclasses/categories'),
  createCategory: (data) => api.post('/onlineclasses/categories', data),
};

// Reports endpoints
export const reports = {
  get: (params) => api.get('/reports', { params }),
};

// GymSettings endpoints
export const gymSettings = {
  get: () => api.get('/gymsettings'),
  update: (data) => api.put('/gymsettings', data),
  releaseSlots: (startDate, endDate) => api.post('/gymsettings/release-slots', { startDate, endDate }),
  generateSlots: (data) => api.post('/gymsettings/generate-slots', data),
};

// ServiceTypes endpoints
export const serviceTypes = {
  getAll: () => api.get('/servicetypes'),
  getById: (id) => api.get(`/servicetypes/${id}`),
  create: (data) => api.post('/servicetypes', data),
  update: (id, data) => api.put(`/servicetypes/${id}`, data),
  delete: (id) => api.delete(`/servicetypes/${id}`),
};

// Waitlist endpoints
export const waitlist = {
  join: (timeSlotId, notes) => api.post('/waitlist', { timeSlotId, notes }),
  leave: (id) => api.delete(`/waitlist/${id}`),
  getMy: () => api.get('/waitlist/my'),
  accept: (id) => api.post(`/waitlist/${id}/accept`),
  getForSlot: (timeSlotId) => api.get(`/waitlist/slot/${timeSlotId}`),
  process: (timeSlotId) => api.post(`/waitlist/process/${timeSlotId}`),
  // Colocar um membro na lista de espera ao balcão (Admin): mesmas condições da entrada própria.
  onBehalf: (data) => api.post('/waitlist/on-behalf', data),
};

// Fitness / Wearables endpoints (P0-B) — dashboard canónico + RGPD
export const fitness = {
  getProviders: () => api.get('/fitness/providers'),
  getDashboard: (from, to) => api.get('/fitness/dashboard', { params: { from, to } }),
  connect: (data) => api.post('/fitness/connect', data),
  disconnect: (provider) => api.post('/fitness/disconnect', null, { params: { provider } }),
  deleteHealthData: (provider) => api.post('/fitness/delete-health-data', null, { params: { provider } }),
  exportData: () => api.get('/fitness/export'),
  setGoal: (data) => api.post('/fitness/goals', data),
  sync: (provider) => api.post('/fitness/sync', null, { params: { provider } }),
  // Huawei OAuth (server-side): status (configured/connected), início do fluxo e conclusão do callback.
  huaweiStatus: () => api.get('/fitness/huawei/status'),
  huaweiAuthorize: () => api.get('/fitness/huawei/authorize'),
  huaweiCallback: (state, code) => api.post('/fitness/huawei/callback', { state, code }),
};

// Perfil operacional / módulos (capacidades por ginásio)
export const gymModules = {
  get: () => api.get('/gymmodules'),
  capabilities: () => api.get('/gymmodules/capabilities'),
  toggle: (feature, enabled) => api.put(`/gymmodules/${feature}`, { enabled }),
  applyPreset: (name) => api.post(`/gymmodules/preset/${name}`),
  audit: (take = 50) => api.get('/gymmodules/audit', { params: { take } }),
};

// Retenção / risco de abandono — capability Retention
export const retention = {
  dashboard: () => api.get('/retention/dashboard'),
  atRisk: (params) => api.get('/retention/at-risk', { params }),
  member: (userId) => api.get(`/retention/members/${userId}`),
  resolve: (userId) => api.post(`/retention/members/${userId}/resolve`),
  tasks: (status = 'open') => api.get('/retention/tasks', { params: { status } }),
  createTask: (data) => api.post('/retention/tasks', data),
  completeTask: (id) => api.post(`/retention/tasks/${id}/complete`),
  settings: () => api.get('/retention/settings'),
  updateSettings: (data) => api.put('/retention/settings', data),
  run: () => api.post('/retention/run'),
};

// Home do sócio — agregador capability-aware (uma chamada, leve)
export const client = {
  home: () => api.get('/client/home'),
};

// Personal Training (pacotes/sessões/comissões/dashboard) — capability PersonalTraining
export const personalTraining = {
  dashboard: (includeFinance = false) => api.get('/personaltraining/dashboard', { params: { includeFinance } }),
  createPackage: (data) => api.post('/personaltraining/packages', data),
  package: (id) => api.get(`/personaltraining/packages/${id}`),
  myPackages: () => api.get('/personaltraining/me/packages'),
  createSession: (data) => api.post('/personaltraining/sessions', data),
  sessionAction: (id, action, data) => api.post(`/personaltraining/sessions/${id}/${action}`, data || {}),
  sessions: (params) => api.get('/personaltraining/sessions', { params }),
  mySessions: () => api.get('/personaltraining/me/sessions'),
  commissionRules: () => api.get('/personaltraining/commission-rules'),
  upsertCommissionRule: (data) => api.post('/personaltraining/commission-rules', data),
  settings: () => api.get('/personaltraining/settings'),
  updateSettings: (data) => api.put('/personaltraining/settings', data),
};

// Planos (atribuição versionada) + execução de treino — capability TrainingPlans
export const training = {
  assign: (userId, workoutPlanId) => api.post('/training/assign', { userId, workoutPlanId }),
  activePlan: (userId) => api.get(`/training/members/${userId}/plan`),
  planHistory: (userId) => api.get(`/training/members/${userId}/plan-history`),
  logExecution: (data) => api.post('/training/me/executions', data),
  myExecutions: () => api.get('/training/me/executions'),
  memberExecutions: (userId) => api.get(`/training/members/${userId}/executions`),
  exerciseHistory: (exercise) => api.get('/training/me/exercise-history', { params: { exercise } }),
};

// Avaliações físicas + progresso — capability PhysicalAssessments / ClientProgress
export const assessments = {
  create: (data) => api.post('/assessments', data),
  forMember: (userId) => api.get(`/assessments/members/${userId}`),
  mine: () => api.get('/assessments/me'),
  addMeasurement: (data) => api.post('/assessments/progress', data),
  memberProgress: (userId, metric) => api.get(`/assessments/members/${userId}/progress`, { params: { metric } }),
  myProgress: (metric) => api.get('/assessments/me/progress', { params: { metric } }),
};

// Biblioteca de exercícios e equipamento. Ler é para qualquer utilizador autenticado; escrever é de
// um Admin ou de um perfil com canManageExerciseLibrary (o servidor decide: responde 403 aos outros).
const uploadForm = (file, { altText, makePrimary, origin, license, attribution, sourceUrl, thumb } = {}) => {
  const form = new FormData();
  form.append('file', file);
  if (altText) form.append('altText', altText);
  form.append('makePrimary', makePrimary ? 'true' : 'false');
  if (origin) form.append('origin', origin);
  if (license) form.append('license', license);
  if (attribution) form.append('attribution', attribution);
  if (sourceUrl) form.append('sourceUrl', sourceUrl);
  if (thumb) form.append('thumb', thumb, thumb.name || 'thumb.webp');
  return form;
};
const multipart = { headers: { 'Content-Type': 'multipart/form-data' } };
// O contrato da media ÚNICA do exercício: campo `file` e, opcionalmente, `alt` (o servidor impõe 25 MB e os tipos).
const singleMediaForm = (file, { alt } = {}) => {
  const form = new FormData();
  if (file) form.append('file', file, file.name);
  if (alt) form.append('alt', alt);
  return form;
};
const batchForm = (files, manifest) => {
  const form = new FormData();
  Array.from(files || []).forEach((f) => form.append('files', f, f.name));
  if (manifest && manifest.length) form.append('manifest', JSON.stringify(manifest));
  return form;
};

export const exerciseLibrary = {
  search: (params) => api.get('/exercise-library/search', { params }),
  meta: () => api.get('/exercise-library/meta'),
  get: (id) => api.get(`/exercise-library/${id}`),
  // Sugestões de substituto: curadas primeiro e, se faltarem, equivalentes propostos pelo motor (com motivo).
  suggestedSubstitutes: (id, take = 6) => api.get(`/exercise-library/${id}/substitutes/suggested`, { params: { take } }),
  create: (data) => api.post('/exercise-library', data),
  update: (id, data) => api.put(`/exercise-library/${id}`, data),
  remove: (id) => api.delete(`/exercise-library/${id}`),
  reset: (id) => api.post(`/exercise-library/${id}/reset`),
  addMedia: (id, file, options) => api.post(`/exercise-library/${id}/media`, uploadForm(file, options), {
    ...multipart,
    // Progresso do envio (importa no telemóvel, onde um vídeo de 30 MB demora): o ecrã mostra a percentagem.
    onUploadProgress: options?.onProgress,
  }),
  updateMedia: (id, mediaId, data) => api.put(`/exercise-library/${id}/media/${mediaId}`, data),
  removeMedia: (id, mediaId) => api.delete(`/exercise-library/${id}/media/${mediaId}`),
  // A media ÚNICA do exercício (um ficheiro do ginásio que substitui o que lá estava): devolve o exercício
  // inteiro já com a media nova. Um vídeo entra POR APROVAR — só os atletas o veem depois de uma pessoa o aprovar.
  uploadSingleMedia: (id, file, options) => api.post(`/library/exercises/${id}/media`, singleMediaForm(file, options), {
    ...multipart,
    onUploadProgress: options?.onProgress,
  }),
  removeSingleMedia: (id) => api.delete(`/library/exercises/${id}/media`),
  // Vídeo incorporado do YouTube (a segunda forma de demonstração): guarda o endereço — nada é descarregado — e devolve
  // o exercício inteiro (media e a demonstração em uso, com a razão). Entra POR VALIDAR; um vídeo próprio aprovado ganha.
  addEmbed: (id, url, channel) => api.post(`/exercise-library/${id}/embed`, { url, channel: channel || null }),
  removeEmbed: (id) => api.delete(`/exercise-library/${id}/embed`),
  // Decisão de uma pessoa sobre um ficheiro do ginásio: { decision: 'approve' | 'reject' | 'pending', note }.
  reviewMedia: (id, mediaId, decision, note) => api.post(`/exercise-library/${id}/media/${mediaId}/review`, { decision, note: note || null }),
  // A fila de validação: o que espera uma pessoa, de todos os exercícios (status = pending | rejected).
  mediaQueue: (params = {}) => api.get('/exercise-library/media/pending', { params }),
  // Decidir muitos ficheiros de uma vez: { decision, note, items: [{ exerciseId, mediaId }] }. O servidor
  // regista, em cada ficheiro, quem decidiu e quando, e devolve o que ficou item a item.
  reviewMediaBatch: (decision, note, items) => api.post('/exercise-library/media/review', { decision, note: note || null, items }),
  // Importação de ativos em lote (imagens e vídeos), idempotente por conteúdo: a pré-visualização não grava nada.
  mediaImportPreview: (files, manifest) => api.post('/exercise-library/media/import/preview', batchForm(files, manifest), multipart),
  mediaImport: (files, manifest) => api.post('/exercise-library/media/import', batchForm(files, manifest), multipart),
  // Importação em lote (só cria; os duplicados ficam de fora): a pré-visualização não grava nada.
  importPreview: (items, includeProbable = false) => api.post('/exercise-library/import/preview', { items, includeProbable }),
  importApply: (items, includeProbable = false) => api.post('/exercise-library/import', { items, includeProbable }),
  // Demonstrações de domínio público (free-exercise-db, Unlicense). Sem `targets`, a correspondência sai do nome
  // em inglês e dos alternativos e o que não casar com segurança fica de fora. A pré-visualização não grava nada;
  // no `openImport` entra tudo POR VALIDAR — só uma pessoa aprova, no editor do exercício.
  openMediaPreview: (params = {}) => api.post('/exercise-library/media/import/open/preview', params),
  openMediaImport: (params = {}) => api.post('/exercise-library/media/import/open', params),
  // Mini vídeos de demonstração de uma fonte aberta: wger.de (Workout Manager; vídeos CC-BY-SA com LICENÇA e
  // AUTOR indicados item a item) e Wikimedia Commons (vídeos com licença LIVRE indicada ficheiro a ficheiro —
  // CC BY/BY-SA, CC0 ou domínio público). O campo `source` escolhe a fonte (`'commons'`); sem ele é o wger.de.
  // A pré-visualização não descarrega nem grava nada; no `openVideoImport` entra tudo POR VALIDAR — só uma
  // pessoa vê o vídeo e aprova, no editor do exercício.
  openVideoPreview: (params = {}) => api.post('/exercise-library/media/import/open-video/preview', params),
  openVideoImport: (params = {}) => api.post('/exercise-library/media/import/open-video', params),
};

export const equipment = {
  search: (params) => api.get('/equipment', { params }),
  get: (id) => api.get(`/equipment/${id}`),
  create: (data) => api.post('/equipment', data),
  update: (id, data) => api.put(`/equipment/${id}`, data),
  remove: (id) => api.delete(`/equipment/${id}`),
  reset: (id) => api.post(`/equipment/${id}/reset`),
  setAvailability: (id, available) => api.put(`/equipment/${id}/availability`, { available }),
  setAvailabilityBulk: (ids, available) => api.put('/equipment/availability', { ids, available }),
  addMedia: (id, file, options) => api.post(`/equipment/${id}/media`, uploadForm(file, options), multipart),
  updateMedia: (id, mediaId, data) => api.put(`/equipment/${id}/media/${mediaId}`, data),
  removeMedia: (id, mediaId) => api.delete(`/equipment/${id}/media/${mediaId}`),
};

// Operação (dashboard adaptativo, receção, perfil 360º) — tudo capability-aware no backend
export const operations = {
  dashboard: () => api.get('/operations/dashboard'),
  reception: () => api.get('/operations/reception'),
  member360: (id) => api.get(`/operations/members/${id}/360`),
};

export default api;

// Player de treino do sócio — execução ao vivo (Bloco 3). Os caminhos são os do WorkoutExecutionController;
// há um teste no servidor que os compara com as rotas reais.
export const workoutExecution = {
  today: (date, dayId) => api.get('/training/me/today', { params: date ? { date, ...(dayId ? { dayId } : {}) } : undefined }),
  active: () => api.get('/training/me/executions/active'),
  start: (data) => api.post('/training/executions', data),
  get: (executionId) => api.get(`/training/executions/${executionId}`),
  saveSet: (executionId, exerciseId, data) => api.post(`/training/executions/${executionId}/exercises/${exerciseId}/sets`, data),
  completeExercise: (executionId, exerciseId) => api.post(`/training/executions/${executionId}/exercises/${exerciseId}/complete`),
  skipExercise: (executionId, exerciseId, reason) => api.post(`/training/executions/${executionId}/exercises/${exerciseId}/skip`, { reason: reason || null }),
  // O «como foi este exercício?»: more | less | never. Uma resposta por atleta e exercício (responder outra vez substitui).
  feedback: (executionId, exerciseId, verdict) => api.post(`/training/executions/${executionId}/exercises/${exerciseId}/feedback`, { verdict }),
  substitute: (executionId, exerciseId, libraryExerciseId, reason) =>
    api.post(`/training/executions/${executionId}/exercises/${exerciseId}/substitute`, { libraryExerciseId, reason: reason || null }),
  // Acrescentar um exercício que o plano não trazia (fica no fim, sem prescrição).
  addExercise: (executionId, libraryExerciseId) =>
    api.post(`/training/executions/${executionId}/exercises`, { libraryExerciseId }),
  complete: (executionId, notes) => api.post(`/training/executions/${executionId}/complete`, { notes: notes || null }),
  abandon: (executionId, reason) => api.post(`/training/executions/${executionId}/abandon`, { reason: reason || null }),
  history: (skip = 0, take = 20) => api.get('/training/me/history', { params: { skip, take } }),
  records: () => api.get('/training/me/records'),
  // Séries por músculo principal numa janela (a semana, calculada no cliente, para bater com as barras do Progresso).
  weeklySets: (from, to) => api.get('/training/me/weekly-sets', { params: { from, to } }),
  // Recuperação por músculo (dias desde o último treino). Com userId, a de um sócio (PT/Admin).
  muscleRecovery: (userId) => (userId
    ? api.get(`/training/members/${userId}/muscle-recovery`)
    : api.get('/training/me/muscle-recovery')),
  // Corrigir ou apagar uma série registada, num treino em curso.
  updateSet: (executionId, exerciseId, setId, patch) => api.patch(`/training/executions/${executionId}/exercises/${exerciseId}/sets/${setId}`, patch),
  deleteSet: (executionId, exerciseId, setId) => api.delete(`/training/executions/${executionId}/exercises/${exerciseId}/sets/${setId}`),
  // Histórico real de um exercício da biblioteca: sessões, carga, volume e frequência.
  exerciseHistory: (libraryExerciseId, take = 10) => api.get(`/training/me/exercises/${libraryExerciseId}/history`, { params: { take } }),
  // A ficha de um exercício da biblioteca (músculos, equipamento, passos, media): usada para ver uma alternativa antes de a escolher.
  libraryExercise: (id) => api.get(`/exercise-library/${id}`),
  // Semana leve: o estado (e a sugestão, que mostra há quantas semanas o atleta treina sem parar) e a decisão de uma
  // pessoa. Marcar e desmarcar são explícitos e reversíveis; ler não altera nada.
  deload: (weekOf, userId) => (userId
    ? api.get(`/training/members/${userId}/deload`, { params: weekOf ? { weekOf } : undefined })
    : api.get('/training/me/deload', { params: weekOf ? { weekOf } : undefined })),
  markDeload: (weekOf, userId) => (userId
    ? api.post(`/training/members/${userId}/deload`, null, { params: weekOf ? { weekOf } : undefined })
    : api.post('/training/me/deload', null, { params: weekOf ? { weekOf } : undefined })),
  unmarkDeload: (weekOf, userId) => (userId
    ? api.delete(`/training/members/${userId}/deload`, { params: weekOf ? { weekOf } : undefined })
    : api.delete('/training/me/deload', { params: weekOf ? { weekOf } : undefined })),
};

// Lembretes de treino do atleta por notificação (PWA). As rotas são as do TrainingRemindersController;
// a chave pública VAPID vem do servidor, nunca do bundle.
export const trainingReminders = {
  settings: () => api.get('/training/reminders/settings'),
  updateSettings: (data) => api.put('/training/reminders/settings', data),
  subscribe: (data) => api.post('/training/reminders/subscribe', data),
  unsubscribe: (endpoint) => api.post('/training/reminders/unsubscribe', { endpoint }),
};

// WhatsApp do ginásio (módulo WhatsApp): envio ao balcão e estado da ligação do número.
export const whatsapp = {
  status: () => api.get('/whatsapp/status'),
  qrcode: () => api.get('/whatsapp/qrcode'),
  connect: (data) => api.post('/whatsapp/connect', data || {}),
  send: (data) => api.post('/whatsapp/send', data),
};

// Contacto de um membro pelo CRM (Admin): SMS e WhatsApp para o número que está no registo.
// O envio sai pelo MagSend e fica registado como interação do membro. O ginásio tem de ter o
// módulo do canal ligado — desligado, a API responde 403 com error: 'feature-disabled'.
export const crm = {
  memberSms: (userId, text) => api.post(`/crm/members/${userId}/sms`, { text }),
  memberWhatsApp: (userId, text) => api.post(`/crm/members/${userId}/whatsapp`, { text }),
};

// Sócios inativos e relatório semanal (Admin). Os sinais vêm do servidor: treinos concluídos e
// check-ins/presenças. O limiar ("inativo depois de N semanas") é a definição do ginásio e chega
// no próprio relatório; muda-se por `gymSettings.update({ inactiveAfterWeeks })`.
export const memberInsights = {
  inactive: () => api.get('/member-insights/inactive'),
  weekly: () => api.get('/member-insights/weekly'),
  emailWeekly: () => api.post('/member-insights/weekly/email'),
};

// Controlo de acessos — o registo de entrada ao balcão. O staff pode indicar o membro (`userId`); o membro
// faz a sua própria entrada (o servidor usa o utilizador da sessão). `method: 'Manual'` = registado na receção.
export const access = {
  gymEntry: (data) => api.post('/access/gym-entry', data),
  manualOverride: (data) => api.post('/access/manual-override', data),
  events: (params) => api.get('/access/events', { params }),
};

// Pedidos de suporte do ginásio ao operador da plataforma (lado do GINÁSIO). O pedido fica
// registado no portal; aqui só se abre, se lê a conversa e se responde. O ginásio vem da sessão
// — nunca de um campo do corpo.
export const supportTickets = {
  list: () => api.get('/support/tickets'),
  get: (id) => api.get(`/support/tickets/${id}`),
  create: (data) => api.post('/support/tickets', data),
  reply: (id, body) => api.post(`/support/tickets/${id}/messages`, { body }),
};
